import React from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";

/**
 * Generic React error boundary.
 *
 * Catches render-time exceptions from any descendant so a single bad
 * state update (e.g. a malformed SSE payload) can't blank the whole
 * inbox. The "Try again" button resets internal state — the page's
 * own effects will then re-mount and re-fetch. For app-wide coverage
 * we also wrap at the route level, but per-page boundaries give a
 * nicer UX (local recovery, not a full re-route).
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Send to console for local debugging; a future integration could
    // ship this to Sentry/LogRocket. Keep the message short so CI
    // logs stay searchable ("Inbox crash:" is the canonical prefix).
    // eslint-disable-next-line no-console
    console.error("Inbox crash:", error, info);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      const name = this.props.label || "this panel";
      return (
        <div
          data-testid="error-boundary-fallback"
          className="flex flex-col items-center justify-center gap-3 p-8 text-center"
        >
          <AlertTriangle className="w-10 h-10 text-amber-500" />
          <p className="text-sm text-muted-foreground max-w-sm">
            Something went wrong loading {name}. Your data is safe — this was a
            display-side error.
          </p>
          <button
            type="button"
            onClick={this.handleRetry}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-border text-sm hover:bg-muted transition-colors"
            data-testid="error-boundary-retry"
          >
            <RefreshCw className="w-4 h-4" />
            Try again
          </button>
          {process.env.NODE_ENV !== "production" && this.state.error && (
            <pre className="text-xs text-red-600 dark:text-red-400 mt-2 max-w-full overflow-auto">
              {String(this.state.error?.message || this.state.error)}
            </pre>
          )}
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
